import React from "react";
import { Helmet } from 'react-helmet-async';
import { BASE_URL } from "../services/config";
import { useLocation } from 'react-router-dom';
import { Card, CardBody, Button, Link, Input } from "@heroui/react";
import NewsletterSection from "../components/newsletter-section";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { useNewsList, useNewsCategories } from "../hooks/use-news";
import NewsBanner from '../assets/images/news-banner.png';

interface NewsItem {
  id: number;
  slug?: string;
  title: string;
  excerpt: string;
  date: string;
  image: string;
  category: string;
}

interface NewsListPageProps {
  onNewsSelect: (newsId: string | number) => void;
}

export const NewsListPage: React.FC<NewsListPageProps> = ({ onNewsSelect }) => {
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 9;

  // Use the news hook instead of static data
  // NOTE: we intentionally do not pass selectedCategory to the API layer because
  // the backend does not support server-side filtering by category. We will
  // filter the full list client-side instead.
  const { news, pagination, isLoading, error, isApiError } = useNewsList(
    currentPage,
    itemsPerPage,
    null
  );
  
  // Filter news by search query and category client-side
  const filteredNews = React.useMemo(() => {
    let list = news || [];

    // Apply category filter (client-side). selectedCategory is a category.code string or null
    if (selectedCategory) {
      list = list.filter(item => String(item.categoryCode || item.category || '').toLowerCase() === String(selectedCategory).toLowerCase());
    }

    if (!searchQuery.trim()) return list;

    return list.filter(item =>
      (item.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.excerpt || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [news, searchQuery, selectedCategory]);

  // Load categories from API (codes + names)
  const { categories: apiCategories, isLoading: categoriesLoading } = useNewsCategories();

  // UI categories: use API categories when available, otherwise derive from current news
  const categories = React.useMemo(() => {
    // Build a set of category codes actually present in the loaded news items
    const presentCodes = new Set(
      (news || [])
        .map(item => String(item.categoryCode || item.category || '').toLowerCase().trim())
        .filter(Boolean)
    );

    // If API provided categories, keep only those that have at least one news item
    if (apiCategories && apiCategories.length > 0) {
      return apiCategories.filter(cat => {
        const code = String(cat.code || cat.name || '').toLowerCase().trim();
        return presentCodes.has(code);
      });
    }

    if (!news || news.length === 0) return [];

    // Derive categories from news items if API categories are not available
    const unique = Array.from(new Set(news.map(item => item.category || item.categoryCode))).map(name => ({ id: null, name, code: String(name).toLowerCase(), description: '' }));
    return unique;
  }, [apiCategories, news]);
  
  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Generate array of page numbers for pagination
  const pageNumbers = React.useMemo(() => {
    const totalPages = pagination.pages;
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    // If current page is among the first 3
    if (currentPage <= 3) {
      return [1, 2, 3, 4, '...', totalPages];
    }
    
    // If current page is among the last 3
    if (currentPage >= totalPages - 2) {
      return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    
    // If current page is in the middle
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  }, [currentPage, pagination.pages]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
        when: "beforeChildren"  // This ensures container becomes visible before children start animating
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"  // Simplified easing function
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Новости и события — SAFONOVA</title>
        <meta name="description" content="Последние новости, события и специальные предложения бренда SAFONOVA" />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || '/news'}`} />
        <meta property="og:title" content="Новости и события — SAFONOVA" />
        <meta property="og:description" content="Последние новости, события и специальные предложения бренда SAFONOVA" />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || '/news'}`} />
      </Helmet>
      {/* Hero Banner */}
      <section 
        className="relative flex items-center py-32 md:py-40"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.18)), url('${NewsBanner}')`,
          backgroundPosition: "center 70%",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat"
        }}
      >
        <div className="container mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white text-shadow">
              Новости и <span className="font-semibold">события</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl text-shadow">
              Следите за последними новостями, мероприятиями и специальными предложениями нашего бренда
            </p>
          </motion.div>
        </div>
      </section>

      {/* News Content */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          {/* Filter and Search - with API error indicator */}
          <div className="flex flex-col md:flex-row gap-6 mb-8">
            <div className="flex-grow">
              <Input
                placeholder="Поиск новостей..."
                value={searchQuery}
                onValueChange={setSearchQuery}
                startContent={<Icon icon="lucide:search" className="text-foreground/50" width={18} />}
                className="max-w-md"
              />
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button
                variant={selectedCategory === null ? "flat" : "light"}
                color={selectedCategory === null ? "primary" : "default"}
                onPress={() => {
                  setSelectedCategory(null);
                  setCurrentPage(1);
                }}
                className="text-foreground/80 hover:text-foreground transition-colors"
              >
                Все
              </Button>
              {categories.map(category => (
                <Button
                  key={category.id ?? category.code}
                  variant={selectedCategory === category.code ? "flat" : "light"}
                  color={selectedCategory === category.code ? "primary" : "default"}
                  onPress={() => {
                    setSelectedCategory(category.code);
                    setCurrentPage(1);
                  }}
                  className="text-foreground/80 hover:text-foreground transition-colors"
                >
                  {category.name}
                </Button>
              ))}
            </div>
          </div>
          
          {/* NOTE: Removed the generic API fallback banner — list page shows empty/error states below when needed. */}

          {isLoading ? (
            // Loading state
            <motion.div 
                className="grid gap-8"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}
                initial={{ opacity: 0.6 }}
                animate={{ opacity: 1 }}
              >
              {Array(itemsPerPage).fill(0).map((_, index) => (
                <Card key={index} className="border border-divider bg-content2/50 w-full h-full">
                  <CardBody className="p-0">
                    <div className="h-48 bg-content3 animate-pulse"></div>
                    <div className="p-6">
                      <div className="h-3 w-20 bg-content3 rounded animate-pulse mb-4"></div>
                      <div className="h-5 bg-content3 rounded animate-pulse mb-3 w-3/4"></div>
                      <div className="h-4 bg-content3 rounded animate-pulse mb-2 w-full"></div>
                      <div className="h-4 bg-content3 rounded animate-pulse mb-4 w-2/3"></div>
                      <div className="h-4 bg-content3 rounded animate-pulse w-1/4 mt-6"></div>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </motion.div>
          ) : filteredNews && filteredNews.length > 0 ? (
            // Fixed animation - moved from motion.div to div to ensure content is always visible
            <div className="grid gap-8" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
              {filteredNews.map((item) => (
                <Card 
                  key={item.id}
                  className="border border-divider bg-content2/50 w-full h-full product-card-hover"
                  isPressable
                  onPress={() => onNewsSelect(item.slug ?? item.id)}
                  disableRipple
                >
                  <CardBody className="p-0 overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={item.image} 
                        alt={item.title} 
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                      <div className="absolute top-4 left-4 bg-background/80 backdrop-blur-sm px-3 py-1">
                        <p className="text-xs font-medium">{item.category}</p>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="text-xs text-foreground/60 mb-2">{item.date}</p>
                      <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                      <p className="text-foreground/70 text-sm mb-4 line-clamp-3">{item.excerpt}</p>
                      
                      <Link 
                        className="text-primary hover:text-primary-600 hover:underline flex items-center gap-1 transition-all duration-200"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onNewsSelect(item.slug ?? item.id);
                          }}
                      >
                        Читать далее <Icon icon="lucide:arrow-right" width={16} />
                      </Link>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          ) : (
            // No results found
            <div className="text-center py-16">
              <Icon icon="lucide:search-x" className="mx-auto mb-4 text-foreground/40" width={48} />
              <h3 className="text-xl font-medium mb-2">Новости не найдены</h3>
              <p className="text-foreground/70 mb-6">
                Попробуйте изменить параметры поиска или выбрать другую категорию
              </p>
              <Button 
                color="primary" 
                variant="flat"
                onPress={() => {
                  setSelectedCategory(null);
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
              >
                Сбросить фильтры
              </Button>
            </div>
          )}

          {/* Dynamic Pagination based on API response */}
          {!isLoading && filteredNews.length > 0 && pagination.pages > 1 && (
            <div className="flex justify-center mt-16">
              <div className="flex flex-wrap gap-2">
                <Button
                  isIconOnly
                  variant="light"
                  color="default"
                  aria-label="Previous page"
                  isDisabled={currentPage === 1}
                  className="text-primary hover:text-white hover:bg-primary/80 transition-colors duration-200 ease-in-out"
                  onPress={() => handlePageChange(currentPage - 1)}
                >
                  <Icon icon="lucide:chevron-left" width={16} />
                </Button>
                
                {pageNumbers.map((page, index) => (
                  typeof page === 'number' ? (
                    <Button
                      key={index}
                      variant={currentPage === page ? "flat" : "light"}
                      color={currentPage === page ? "primary" : "default"}
                      aria-label={`Page ${page}`}
                      className="text-primary hover:text-white hover:bg-primary/80 transition-colors duration-200 ease-in-out"
                      onPress={() => handlePageChange(page)}
                    >
                      {page}
                    </Button>
                  ) : (
                    <Button
                      key={index}
                      isIconOnly
                      variant="light"
                      color="default"
                      isDisabled
                      className="text-foreground/50"
                    >
                      ...
                    </Button>
                  )
                ))}
                
                <Button
                  isIconOnly
                  variant="light"
                  color="default"
                  aria-label="Next page"
                  isDisabled={currentPage === pagination.pages}
                  className="text-primary hover:text-white hover:bg-primary/80 transition-colors duration-200 ease-in-out"
                  onPress={() => handlePageChange(currentPage + 1)}
                >
                  <Icon icon="lucide:chevron-right" width={16} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>
      
      {/* Subscribe Section */}
      <NewsletterSection />
    </>
  );
};